import React, { useRef, useState } from "react";
import { Stage, Layer, Image as KonvaImage, Circle } from "react-konva";
import useImage from "use-image";

const STAGE_WIDTH = 900;
const STAGE_HEIGHT = 600;

export default function TimesheetAnnotator({ imageUrl, originalFileName, onSave, onClose }) {
  const stageRef = useRef(null);
  const isDrawing = useRef(false);

  const [image] = useImage(imageUrl);
  const [annotations, setAnnotations] = useState([]);
  const [history, setHistory] = useState([]);
  const [redoStack, setRedoStack] = useState([]);

  const pushHistory = (next) => {
    setHistory((h) => [...h, annotations]);
    setRedoStack([]);
    setAnnotations(next);
  };

  const handleMouseDown = (e) => {
    const pos = stageRef.current.getPointerPosition();
    if (!pos) return;

    isDrawing.current = true;

    pushHistory([
      ...annotations,
      {
        id: Date.now(),
        x: pos.x,
        y: pos.y,
        radius: 1,
      },
    ]);
  };

  const handleMouseMove = () => {
    if (!isDrawing.current) return;

    const pos = stageRef.current.getPointerPosition();
    if (!pos) return;

    setAnnotations((prev) => {
      const last = prev[prev.length - 1];
      const dx = pos.x - last.x;
      const dy = pos.y - last.y;
      const r = Math.sqrt(dx * dx + dy * dy);

      const updated = [...prev];
      updated[updated.length - 1] = { ...last, radius: r };
      return updated;
    });
  };

  const handleMouseUp = () => {
    isDrawing.current = false;
  };

  const undo = () => {
    if (!history.length) return;
    setRedoStack((r) => [annotations, ...r]);
    setAnnotations(history[history.length - 1]);
    setHistory((h) => h.slice(0, -1));
  };

  const redo = () => {
    if (!redoStack.length) return;
    setHistory((h) => [...h, annotations]);
    setAnnotations(redoStack[0]);
    setRedoStack((r) => r.slice(1));
  };

  // 🔥 THIS is the critical part
  const saveAnnotatedImage = () => {
    console.log("Annotations:", annotations);
    const uri = stageRef.current.toDataURL({ pixelRatio: 2 });

    fetch(uri)
      .then(res => res.blob())
      .then(blob => {
        const fileName = originalFileName || "timesheet.png";
        const file = new File(
          [blob],
          fileName,
          { type: "image/png" }
        );

        onSave(file);
      });
  };

  return (
    <div style={{ width: "100%", height: "100%", position: "relative" }}>
      {/* Top-right controls */}
      <div style={{
        position: "absolute",
        top: 10,
        right: 10,
        zIndex: 10,
        display: "flex",
        gap: 10
      }}>
        <button onClick={undo} title="Undo">↩️</button>
        <button onClick={redo} title="Redo">↪️</button>
        <button onClick={saveAnnotatedImage} title="Save">💾</button>
        <button onClick={onClose} title="Close">❌</button>
      </div>

      <Stage
        ref={stageRef}
        width={STAGE_WIDTH}
        height={STAGE_HEIGHT}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        style={{
          cursor: "crosshair",
          border: "1px solid #ccc",
          background: "#f5f5f5"
        }}
      >
        <Layer>
          {image && (
            <KonvaImage
              image={image}
              width={STAGE_WIDTH}
              height={STAGE_HEIGHT}
            />
          )}

          {annotations.map(a => (
            <Circle
              key={a.id}
              x={a.x}
              y={a.y}
              radius={a.radius}
              stroke="red"
              strokeWidth={3}
            />
          ))}
        </Layer>
      </Stage>
    </div>
  );
}
